import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  DEFAULT_FEATURE_FLAGS,
  resolveFlags,
  shouldBypassFlags,
} from '../utils/featureFlags';

/**
 * Real-time feature flags from Firestore `app_config/feature_flags`.
 *
 * God-mode users bypass flags entirely (all features enabled),
 * unless `simulateTechView` is true (POV mode — see what techs see).
 * Falls back to all-enabled on error or missing doc.
 *
 * @param {Object} options
 * @param {boolean} options.simulateTechView - When true, skip god-mode bypass
 * @returns {{ flags, loading, isFeatureEnabled }}
 */
export function useFeatureFlags({ simulateTechView = false } = {}) {
  const { userProfile } = useAuth();
  const [rawFlags, setRawFlags] = useState(null);
  const [loading, setLoading] = useState(true);

  // Subscribe to Firestore doc
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'app_config', 'feature_flags'),
      (snap) => {
        setRawFlags(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        console.error('Feature flags listener error:', err);
        setRawFlags(null);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // God mode sees everything — unless simulating tech view (POV mode)
  const bypass = !simulateTechView && shouldBypassFlags(userProfile);
  const flags = bypass ? { ...DEFAULT_FEATURE_FLAGS } : resolveFlags(rawFlags);

  const isFeatureEnabled = useCallback(
    (key) => bypass || flags[key] === true,
    [bypass, flags]
  );

  return { flags, loading, isFeatureEnabled };
}
